import { useQueryClient } from '@tanstack/react-query';
import React, { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import RootLoader from '../components/root-loader/index';
import { BackendRoute } from '../config/index';
import { QueryKey } from '../constants/index';
import { HttpException } from '../exceptions/index';
import { Company, useAppMutation } from '../hooks/index';
import { acceptDataRoomInvite } from '../hooks/space/index';
import { useClaimSubscriptionOffer } from '../hooks/subscription-offer/use-claim-subscription-offer';
import { ApiService } from '../services/index';
import {
  dataRoomInviteStore,
  inviteStakeholderStore,
  readPendingDataRoomInvite,
} from '../storage/local/index';
import { showOnboardingPageState, subscriptionOfferStore } from '../storage/local/index';
import { PageRoute } from '../types/page.types';
import { buildLoginPathWithRedirect, persistPostAuthRedirect } from '../utils/auth-redirect.util';
import { resolveUnfinishedCompanySessionRedirect } from '../utils/unfinished-company-creation-redirect.util';
import {
  shouldAcceptDataRoomInvite,
  shouldResolveUnfinishedCompany,
} from './auth-bootstrap-order.util';
import { useStakeholderPermissionGuard } from './use-stakeholder-permission-guard';

/** Failsafe when validate/invitation bootstrap never settles (e.g. hung /api proxy). */
const AUTH_BOOTSTRAP_TIMEOUT_MS = 45_000;

interface AuthGuardProps {
  children: ReactNode;
}

const AuthGuard: FC<AuthGuardProps> = ({ children }) => {
  const { claimSubscriptionOffer } = useClaimSubscriptionOffer();
  const navigate = useNavigate();
  const location = useLocation();
  const loginPath = buildLoginPathWithRedirect(`${location.pathname}${location.search}`);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInvitationHandled, setIsInvitationHandled] = useState(false);
  const [isDataRoomInviteHandled, setIsDataRoomInviteHandled] = useState(false);
  const [isOfferHandled, setIsOfferHandled] = useState(false);
  const [isUnfinishedCompanyHandled, setIsUnfinishedCompanyHandled] = useState(false);
  const [isBootstrapTimedOut, setIsBootstrapTimedOut] = useState(false);

  const {
    mutate,
    isPending: isLoading,
    isError: isValidateError,
  } = useAppMutation([BackendRoute.AUTH_VALIDATE], {
    mutationFn: () => ApiService.get({ endpoint: BackendRoute.AUTH_VALIDATE }),
    retry: false,
    onSuccess: () => setIsAuthenticated(true),
    onError: () => {
      void ApiService.clearStaleAuthSession();
    },
    defaultErrorHandling: false,
  });

  const unauthorizedRedirect = useStakeholderPermissionGuard();
  const queryClient = useQueryClient();
  const { setSelectedAsync } = Company.useSetSelected();

  Company.useCheckModuleAccess(isAuthenticated);
  // After the stakeholder invitation, never before it: a link that names a company the person has
  // only just been invited to can be selected once that invitation is accepted, and not a moment
  // earlier. Selecting first answers 404 and sends them away from the page they were sent to.
  const { isSelectingCompany } = Company.useSelectCompanyFromQueryParam(
    isAuthenticated && isInvitationHandled,
  );

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [QueryKey.GET_SELECTED_COMPANY],
    });
    queryClient.invalidateQueries({
      queryKey: [QueryKey.GET_COMPANIES],
    });
  }, [queryClient]);

  const handleInvitation = useCallback(async () => {
    const invitation = inviteStakeholderStore.get();
    if (!invitation?.id || invitation?.isAccepted) {
      setIsInvitationHandled(true);
      return;
    }

    try {
      await ApiService.get({
        endpoint: BackendRoute.INVITATIONS,
        routePath: ['accept', invitation.id],
      });
      inviteStakeholderStore.set({
        ...invitation,
        isAccepted: true,
      });

      invalidateQuery();
    } catch (e) {
      if (e instanceof HttpException) {
        toast.error(e.body?.message);
      }
      inviteStakeholderStore.delete();

      navigate('/error-page');
    } finally {
      setIsInvitationHandled(true);
    }
  }, [invalidateQuery, navigate]);

  /**
   * A data-room invitation waits here rather than on the page that resolved it: accepting needs a
   * session, and getting one takes the person out of the app to confirm their address. The token
   * rides in storage across that trip, exactly as the stakeholder invitation above does.
   */
  const handleDataRoomInvite = useCallback(async () => {
    const invite = readPendingDataRoomInvite();

    if (!invite || invite.isAccepted) {
      setIsDataRoomInviteHandled(true);

      return;
    }

    try {
      const { companyId, dataRoomId } = await acceptDataRoomInvite(invite.token);

      // Kept, not deleted: the room is opened from the Data Room page once the space list, which
      // is where a room's full identity lives, has loaded.
      dataRoomInviteStore.set({ ...invite, dataRoomId, isAccepted: true });

      /**
       * The invitation decides which company the person is looking at, and accepting one does not
       * move the session by itself. Somebody who already belongs to other companies arrives with
       * one of those selected, and the room they were sent to is not among its rooms — so they
       * land in that other company instead, with nothing to show for the link they followed.
       *
       * Awaited before the redirect, and inside the bootstrap gate, so the Data Room page is
       * first drawn for the right company rather than being drawn twice.
       */
      await setSelectedAsync(companyId);

      invalidateQuery();
      navigate(PageRoute.DATA_ROOM_WORKSPACES, { replace: true });

      // The same suppression the offer claim performs: a redirect has just been made towards the
      // place the person actually asked for, and the generic bootstrap one must not overwrite it.
      setIsUnfinishedCompanyHandled(true);
    } catch (e) {
      // Every refusal the server gives here is addressed to the person — a mismatched account, a
      // withdrawn invitation — so it is shown rather than swallowed.
      if (e instanceof HttpException) {
        toast.error(e.body?.message);

        // Only an invitation that never became access is discarded. Once it has, the room is
        // theirs and its id is still worth keeping even if the company switch after it failed:
        // the room then opens the moment they reach that company by any other route.
        if (!dataRoomInviteStore.get()?.isAccepted) {
          dataRoomInviteStore.delete();
        }
      }
    } finally {
      setIsDataRoomInviteHandled(true);
    }
  }, [invalidateQuery, navigate, setSelectedAsync]);

  const handleSubscriptionOffer = useCallback(async () => {
    const offer = subscriptionOfferStore.get();
    if (!offer?.slug) {
      setIsOfferHandled(true);
      return;
    }

    try {
      const { companyId } = await claimSubscriptionOffer(offer.slug);
      subscriptionOfferStore.delete();
      invalidateQuery();
      navigate(`/billing/${companyId}`, { replace: true });
    } catch (e) {
      // Domain mismatch (403) and already-activated (409) both carry a customer-facing message
      // from the backend; surface it and drop the user on My Companies. Transient failures (no
      // HttpException) keep the stored offer so the claim retries on the next load.
      if (e instanceof HttpException) {
        toast.error(e.body?.message);
        subscriptionOfferStore.delete();
      }
      navigate('/my-companies', { replace: true });
    } finally {
      setIsUnfinishedCompanyHandled(true);
      setIsOfferHandled(true);
    }
  }, [claimSubscriptionOffer, invalidateQuery, navigate]);

  const handleUnfinishedCompany = useCallback(async () => {
    if (showOnboardingPageState.get()) {
      setIsUnfinishedCompanyHandled(true);
      return;
    }

    try {
      const redirectTo = await resolveUnfinishedCompanySessionRedirect(location.pathname);
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
      }
    } finally {
      setIsUnfinishedCompanyHandled(true);
    }
  }, [location.pathname, navigate]);

  const hasRequestedValidateRef = useRef(false);

  useEffect(() => {
    if (hasRequestedValidateRef.current) {
      return;
    }
    hasRequestedValidateRef.current = true;
    setIsBootstrapTimedOut(false);
    mutate();
  }, [mutate]);

  const isValidatingSession = isLoading && !isAuthenticated;

  useEffect(() => {
    if (!isLoading && isValidateError) {
      persistPostAuthRedirect(`${location.pathname}${location.search}`);
    }
  }, [isLoading, isValidateError, location.pathname, location.search]);

  useEffect(() => {
    const isStillBootstrapping =
      isValidatingSession ||
      (!isAuthenticated && !isValidateError) ||
      !isInvitationHandled ||
      !isOfferHandled ||
      !isDataRoomInviteHandled ||
      !isUnfinishedCompanyHandled ||
      isSelectingCompany;

    if (!isStillBootstrapping) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsBootstrapTimedOut(true);
    }, AUTH_BOOTSTRAP_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [
    isAuthenticated,
    isDataRoomInviteHandled,
    isInvitationHandled,
    isOfferHandled,
    isUnfinishedCompanyHandled,
    isSelectingCompany,
    isValidatingSession,
    isValidateError,
  ]);

  useEffect(() => {
    if (!isBootstrapTimedOut) {
      return;
    }
    void ApiService.clearStaleAuthSession();
  }, [isBootstrapTimedOut]);

  useEffect(() => {
    const showOnboardingPage = showOnboardingPageState.get();
    if (showOnboardingPage) {
      navigate('/onboarding');
    }
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;

    handleInvitation();
  }, [handleInvitation, isAuthenticated]);

  useEffect(() => {
    if (
      !shouldAcceptDataRoomInvite({
        isAuthenticated,
        isDataRoomInviteHandled,
        isInvitationHandled,
        isOfferHandled,
        isUnfinishedCompanyHandled,
      })
    ) {
      return;
    }

    handleDataRoomInvite();
  }, [
    handleDataRoomInvite,
    isAuthenticated,
    isDataRoomInviteHandled,
    isInvitationHandled,
    isOfferHandled,
    isUnfinishedCompanyHandled,
  ]);

  useEffect(() => {
    if (!isAuthenticated) return;

    handleSubscriptionOffer();
  }, [handleSubscriptionOffer, isAuthenticated]);

  useEffect(() => {
    if (
      !shouldResolveUnfinishedCompany({
        isAuthenticated,
        isDataRoomInviteHandled,
        isInvitationHandled,
        isOfferHandled,
        isUnfinishedCompanyHandled,
      })
    ) {
      return;
    }

    handleUnfinishedCompany();
  }, [
    handleUnfinishedCompany,
    isAuthenticated,
    isDataRoomInviteHandled,
    isInvitationHandled,
    isOfferHandled,
    isUnfinishedCompanyHandled,
  ]);

  if (isBootstrapTimedOut) {
    return <Navigate replace to={loginPath} />;
  }

  if (!isLoading && isValidateError) {
    return <Navigate replace to={loginPath} />;
  }

  if (
    isValidatingSession ||
    !isAuthenticated ||
    !isInvitationHandled ||
    !isOfferHandled ||
    !isDataRoomInviteHandled ||
    !isUnfinishedCompanyHandled ||
    isSelectingCompany
  ) {
    return <RootLoader />;
  }

  if (unauthorizedRedirect) {
    return <Navigate replace to={unauthorizedRedirect} />;
  }

  return <>{children}</>;
};

export default AuthGuard;
